import { useEffect, useState, useCallback } from "react";
import { TextField, Grid, Button, Typography } from "@mui/material";
import md5 from "md5";

import { paths } from "@helios/toolbox-types";
import { Page } from "@mb-pro-ui/components";
import { useIntl } from "react-intl";
import TitledCard from "./templates/basic/TitledCard";
import { useApi } from "../utils/ApiProvider";
import { useUserInfo } from "../utils/user-info";
import { usePageSxProps } from "../pages/styles";
import { APP_NAME } from "../App";

type ChangePassResponse = paths["/profile/change-my-password"]["post"]["responses"]["200"]["content"]["application/json"];

const UserSettings = () => {
  const [currentPass, setCurrentPass] = useState("");
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<undefined | "success.main" | "error">(undefined);

  const api = useApi();
  const [_user, setUser] = useUserInfo();
  const { singlePageSx } = usePageSxProps();
  const { formatMessage } = useIntl();

  const changePass = useCallback(
    (current: string, pass: string, pass2: string) => {
      if (pass !== pass2) {
        setMsgType("error");
        setMsg(formatMessage({ defaultMessage: "Different passwords." }));
        return;
      }
      setMsgType(undefined);
      setMsg("");
      api<ChangePassResponse>("/profile/change-my-password", {
        method: "post",
        body: JSON.stringify({
          current: md5(current),
          new: md5(pass),
        }),
      })
        .then((res) => {
          setUser(res);
          setMsgType("success.main");
          setMsg(formatMessage({ defaultMessage: "Password change was successful." }));
        })
        .catch((err) => {
          setMsgType("error");
          setMsg(formatMessage({ defaultMessage: "Password change failed." }));
          console.error(err);
        });
    },
    [api, setUser, formatMessage]
  );

  useEffect(() => {
    document.title = `${formatMessage({ defaultMessage: "User settings" })} - ${APP_NAME}`;
  }, [formatMessage]);

  return (
    <Page sx={{ ...singlePageSx, alignItems: "center" }}>
      <TitledCard title={formatMessage({ defaultMessage: "Change password" })} style={{ width: 500, maxWidth: "100%" }}>
        <Grid container spacing={1} padding={1}>
          <Grid item xs={12}>
            <TextField
              label={formatMessage({ defaultMessage: "Current password" })}
              variant="filled"
              type="password"
              value={currentPass}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCurrentPass(e.target.value);
              }}
              fullWidth
              size="small"
              sx={{ margin: 0 }}
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={formatMessage({ defaultMessage: "New password" })}
              variant="filled"
              type="password"
              value={pass}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPass(e.target.value);
              }}
              fullWidth
              size="small"
              sx={{ margin: 0 }}
              autoComplete="new-password"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={formatMessage({ defaultMessage: "Confirm new password" })}
              variant="filled"
              type="password"
              value={pass2}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPass2(e.target.value);
              }}
              fullWidth
              size="small"
              sx={{ margin: 0 }}
              autoComplete="new-password"
            />
          </Grid>
          {msg && (
            <Grid item xs={12}>
              <Typography color={msgType} variant="body2">
                {msg}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button variant="text" onClick={() => changePass(currentPass, pass, pass2)} disabled={!currentPass || !pass || !pass2}>
              {formatMessage({ defaultMessage: "Change password" })}
            </Button>
          </Grid>
        </Grid>
      </TitledCard>
    </Page>
  );
};

export default UserSettings;
